import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import mixpanel, { type Config } from 'mixpanel-browser';
import { ENROLL_EVENT_REPORT } from 'src/app/@constants/local-storage-key-constants';
import { EnrollEventReport } from 'src/app/@models/analytics/enroll-event-report.model';
import { getLocalStorageItem } from 'src/app/@utils/local-storage-util';

@Injectable({
  providedIn: 'root',
})
export class MixpanelService {
  constructor() {
    this._initializeUserFromStorage();
  }

  private readonly _document = inject(DOCUMENT);

  private readonly _userId = signal<string | null>(null);
  private readonly _userEmail = signal<string | null>(null);
  private readonly _userEmailDomain = signal<string | null>(null);
  private readonly _enrollmentData = signal<EnrollEventReport | null>(null);
  private readonly _isInitialized = signal<boolean>(false);

  private readonly _userProperties = computed(() => ({
    user_id: this._userId() ?? 'anonymous',
    user_email_domain: this._userEmailDomain() ?? 'unknown',
    has_email: this._userId() ? 'true' : 'false',
    event_id: this._enrollmentData()?.eventNameIdentifier ?? 'unknown',
    wants_notifications:
      this._enrollmentData()?.sendEventReportNotification ?? false,
  }));

  initialize(token: string, options?: Config): void {
    if (this._isInitialized()) {
      return;
    }

    mixpanel.init(token, {
      autocapture: true,
      record_sessions_percent: 100,
      ...options,
    });

    mixpanel.register({
      'App Name': 'Event Insights',
      'App Type': 'Insights Display',
    });

    this._isInitialized.set(true);

    if (this._userId()) {
      this._identifyUser();
    }
  }

  setUserInfo(email: string): void {
    this._userId.set(btoa(email));
    this._userEmail.set(email);
    this._userEmailDomain.set(email.split('@')[1]);

    if (this._isInitialized()) {
      this._identifyUser();
    }
  }

  trackPageView(pagePath: string, pageTitle: string): void {
    if (!this._isInitialized()) {
      return;
    }

    mixpanel.track('Page View', {
      ...this._userProperties(),
      page_path: pagePath,
      page_title: pageTitle,
      page_referrer: this._document.referrer,
    });
  }

  trackEvent(
    eventName: string,
    properties: Record<string, unknown> = {}
  ): void {
    if (!this._isInitialized()) {
      return;
    }

    mixpanel.track(eventName, {
      ...this._userProperties(),
      ...properties,
    });
  }

  trackEngagement(engagementTime: number, engagementType: string): void {
    this.trackEvent('User Engagement', {
      engagement_time_sec: engagementTime,
      engagement_type: engagementType,
    });
  }

  trackScrollDepth(scrollDepth: number): void {
    this.trackEvent('Scroll', {
      scroll_depth: scrollDepth,
    });
  }

  trackDownload(fileName: string, fileType: string): void {
    this.trackEvent('File Download', {
      file_name: fileName,
      file_type: fileType,
    });
  }

  trackFormInteraction(
    formId: string,
    action: 'start' | 'complete' | 'error',
    details?: Record<string, unknown>
  ): void {
    this.trackEvent(`Form ${action}`, {
      form_id: formId,
      form_name: formId,
      ...details,
    });
  }

  trackClick(
    elementId: string,
    elementType: string,
    elementContent?: string
  ): void {
    this.trackEvent('Click', {
      element_id: elementId,
      element_type: elementType,
      element_content: elementContent,
    });
  }

  trackError(
    errorType: string,
    errorMessage: string,
    errorStack?: string
  ): void {
    this.trackEvent('Error', {
      error_type: errorType,
      error_message: errorMessage,
      error_stack: errorStack,
    });
  }

  trackSearch(searchTerm: string, resultCount: number): void {
    this.trackEvent('Search', {
      search_term: searchTerm,
      result_count: resultCount,
    });
  }

  trackVideoInteraction(
    videoId: string,
    action: 'play' | 'pause' | 'complete',
    currentTime: number
  ): void {
    this.trackEvent(`Video ${action}`, {
      video_id: videoId,
      current_time: currentTime,
    });
  }

  private _identifyUser(): void {
    const userId = this._userId();
    if (!userId) {
      return;
    }

    mixpanel.identify(userId);

    const enrollmentData = this._enrollmentData();
    mixpanel.people.set({
      $email: this._userEmail(),
      $email_domain: this._userEmailDomain(),
      event_id: enrollmentData?.eventNameIdentifier ?? 'unknown',
      wants_notifications: enrollmentData?.sendEventReportNotification ?? false,
    });

    mixpanel.people.set_once({
      $created: new Date().toISOString(),
    });
  }

  private _initializeUserFromStorage(): void {
    const storedData =
      getLocalStorageItem<EnrollEventReport>(ENROLL_EVENT_REPORT);
    if (storedData?.email) {
      this._enrollmentData.set(storedData);
      this.setUserInfo(storedData.email);
    }
  }
}
